import { Component, Host, h, Prop, State, Fragment, Listen, Element, Build } from '@stencil/core';
import { DomService, StoryblokService, StringService, VideoService } from '../../services';

import '@vime/core';
import { ButtonAppearance } from '../rcm-button/rcm-button.types';
import { TrackingService } from '../../services/tracking';
import { TrackingObjectBuilder } from '../../services/tracking/tracking-object-builder';
import { RcmImageProp } from '../rcm-image/rcm-image.types';

@Component({
  tag: 'rcm-video',
  styleUrl: 'rcm-video.scss',
  shadow: true,
})
export class RcmVideo {
  @Prop() videoSrc?: string;

  @Prop() videoAsset?: string;

  @Prop() thumbnail?: string | RcmImageProp;

  @Prop() autoplay: boolean = false;

  @Prop() fullWidth?: boolean = false;

  @Prop() mobilePlayFullscreen?: boolean = false;

  @Prop() parentComponent?: string = 'rcm-video';

  @Prop() duration?: string;

  @Prop() increasedMarginTop: boolean = false;

  @State() showPlayButton: boolean = true;

  @State() videoId: string;

  @State() showVideo: boolean;

  @State() isSmallDevice: boolean;

  @State() hasStarted: boolean = false;

  @Element() element: HTMLElement;

  @State() isIOS: boolean;

  player!: any;

  isArticle: boolean = false;

  isVideoAndText: boolean = false;

  @Listen('consent.onetrust', { target: 'window' })
  consentChanged() {
    this.showVideo = VideoService.isUSCookieAccepted() || typeof this.videoAsset === 'string';

    if (this.player) this.showPlayButton = !this.player.playing;
  }

  parseVideoAssets() {
    const videoAssetObject: any = this.videoAsset ? StringService.stringToObject(this.videoAsset) : null;
    this.videoAsset = videoAssetObject?.src;

    this.isIOS = DomService.isIOS();
    const thumbnailObject: any = this.thumbnail && !this.isIOS ? StringService.stringToObject(this.thumbnail) : null;
    this.thumbnail = StoryblokService.imageSrcToDownloadLink(thumbnailObject?.src);

    if (this.videoAsset) this.videoSrc = null;
  }

  componentWillLoad() {
    if (Build.isBrowser) {
      this.showVideo = VideoService.isUSCookieAccepted() || typeof this.videoAsset === 'string';
      this.isArticle = DomService.getGlobalConfig().isArticle === true;
      this.isVideoAndText = this.parentComponent === 'rcm-editorial-big-item-video';
      this.videoId = this.videoSrc ? VideoService.getYoutubeVideoId(this.videoSrc) : null;
      if (!this.videoId) this.parseVideoAssets();
      if (DomService.isIOS()) {
        this.isSmallDevice = true;
      } else {
        DomService.fireWhenBelow('large', () => {
          this.isSmallDevice = true;
        });
        DomService.fireWhenAboveOrEqual('large', () => {
          this.isSmallDevice = false;
        });
      }
    }
  }

  componentDidLoad() {
    if (Build.isBrowser) {
      this.element.shadowRoot.querySelector('vm-player').shadowRoot.querySelector('.player').setAttribute('style', 'min-width:1px');
    }
  }

  onPlayingChange() {
    this.showPlayButton = !this.showPlayButton;

    if (this.mobilePlayFullscreen && !this.showPlayButton && this.isSmallDevice) {
      this.player.enterFullscreen().catch((err) => {});
    }
  }

  onReady() {
    // vm-player autoplay prop does not always work - to be sure it needs to be done programmatically
    // usally to autoplay video needs to be muted, but for our modals it works nevertheless
    // to autoplay other videos than rcm-video-four-blocks, add muted prop to vm-player
    if (this.autoplay) {
      this.player.play();
    }
  }

  onPlaybackStarted() {
    this.hasStarted = true;
    if (this.mobilePlayFullscreen && this.isSmallDevice) {
      this.player.enterFullscreen().catch((err) => {});
    }

    TrackingService.push(
      new TrackingObjectBuilder()
        .forVideoPlayback({
          videoTitle: this.player.mediaTitle,
          parentComponent: this.parentComponent,
        })
        .build(),
    );
  }

  onVideoClick() {
    if (this.player.paused) this.player.play();
    else this.player.pause();
  }

  private onFullscreenChange(event: CustomEvent<boolean>) {
    const isFullscreen = event.detail;
    if (this.mobilePlayFullscreen && !isFullscreen) {
      this.player.pause();
    }
  }

  render() {
    const { cookieAcceptanceText, buttonText } = DomService.getGlobalContent().video?.cookieConsent || {};

    return (
      <Host style={{ marginTop: this.increasedMarginTop ? 'var(--spacings-xl)' : 'var(--spacings-l)' }}>
        <rcm-container fullWidth={this.isArticle || this.fullWidth}>
          <rcm-columns>
            <rcm-column span={12}>
              {this.showVideo ? (
                <vm-player
                  class="video-player"
                  ref={(el: any) => {
                    this.player = el;
                  }}
                  onVmPlayingChange={this.onPlayingChange.bind(this)}
                  onVmReady={this.onReady.bind(this)}
                  onVmPlaybackStarted={this.onPlaybackStarted.bind(this)}
                  onVmFullscreenChange={this.onFullscreenChange.bind(this)}
                >
                  {this.videoId && <vm-youtube videoId={this.videoId} />}

                  {this.videoAsset && !this.videoId && (
                    <vm-video
                      poster={this.thumbnail ?? null}
                      class={`${!this.hasStarted && this.thumbnail ? 'video-opacity' : ''}`}
                      onClick={() => this.onVideoClick()}
                      preload="none"
                    >
                      <source data-src={`${this.videoAsset}`} type="video/mp4" />
                    </vm-video>
                  )}

                  <vm-ui>
                    <vm-poster />
                    <vm-controls fullWidth waitForPlaybackStart hideOnMouseLeave={true} hideWhenPaused={this.mobilePlayFullscreen}>
                      <vm-control-group class="controls">
                        <vm-playback-control tooltipDirection="right" />
                        <vm-time-progress separator="/" />
                        <vm-scrubber-control class="scrubber slider" />
                        <vm-volume-control class="slider" />
                        <vm-fullscreen-control tooltipDirection="left" />
                      </vm-control-group>
                    </vm-controls>
                  </vm-ui>

                  {!this.hasStarted && this.duration && <div class="duration">{this.duration}</div>}
                  {this.showPlayButton && (
                    <button
                      class="play-button center"
                      onClick={() => {
                        this.player.play();
                      }}
                    >
                      <vm-icon name="play" library="material" label="Play" class="play-icon" />
                    </button>
                  )}
                </vm-player>
              ) : (
                <Fragment>
                  <vm-player class="video-player" playsinline controls>
                    <vm-video></vm-video>
                    <vm-ui>
                      <vm-scrim class="scrim" />
                    </vm-ui>
                    <div class="center overlay">
                      <p class={this.isVideoAndText ? 'text-half-width' : 'text-normal'}>{cookieAcceptanceText}</p>
                      <div class="button-container">
                        <rcm-button
                          class="agree"
                          text={buttonText}
                          appearance={ButtonAppearance.PRIMARY_YELLOW}
                          onRcmButtonClick={VideoService.acceptUSCookie}
                        ></rcm-button>
                      </div>
                    </div>
                  </vm-player>
                </Fragment>
              )}
            </rcm-column>
          </rcm-columns>
        </rcm-container>
      </Host>
    );
  }
}
